/**
 * Keeps React alive under the browser's page translation (Chrome/Google Translate, Edge).
 *
 * The translator swaps React's text nodes for its own `<font>` elements. When React later removes
 * or inserts relative to such a detached node, the DOM throws and the app falls to the error page.
 * See https://github.com/facebook/react/issues/11538.
 *
 * A text node React updates after the swap stays detached, so the translated copy goes stale. Mark
 * live values (times, ranks, counters) with `translate="no"` so they are never swapped.
 */
export const guardPageTranslation = (): (() => void) => {
  const { removeChild, insertBefore } = Node.prototype;

  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn('Skipped removeChild of a node the page translator moved', child, this);
      return child;
    }
    return removeChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child != null && child.parentNode !== this) {
      console.warn(
        'Appended a node whose insertBefore anchor the page translator moved',
        child,
        this,
      );
      // Order may be off, but skipping would hide the new content.
      return insertBefore.call(this, node, null) as T;
    }
    return insertBefore.call(this, node, child) as T;
  };

  return () => {
    Node.prototype.removeChild = removeChild;
    Node.prototype.insertBefore = insertBefore;
  };
};
